import React, { Suspense, lazy } from "react";
import {BrowserRouter as Router, Navigate, Route, Routes} from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { PhoneSourceProvider } from "./context/PhoneSourceContext";
import { useViewportHeight } from "./hooks/useViewportHeight";
import PrivateRoute from "./routes/PrivateRoute";
import RoleRoute from "./routes/RoleRoute";
import AppBarLayout from "./layouts/AppBarLayout";
import HomePage from "./pages/HomePage";
import { Box, CircularProgress } from "@mui/material";

// Route-level code splitting: lazy load non-homepage routes to reduce initial bundle (TBT)
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const LoginPage = lazy(() => import("./pages/LoginPage/LoginPage"));
const UserDashboardPage = lazy(() => import("./pages/user/UserDashboardPage"));
const ServiceHistoryPage = lazy(() => import("./pages/user/ServiceHistoryPage"));
const ServiceDetailsPage = lazy(() => import("./pages/user/ServiceDetailsPage"));
const AppointmentsPage = lazy(() => import("./pages/user/AppointmentsPage"));
const PendingQuotesPage = lazy(() => import("./pages/user/PendingQuotesPage"));
const MyProfilePage = lazy(() => import("./pages/user/MyProfilePage"));
const EmailVerificationPage = lazy(() => import("./pages/EmailVerificationPage"));
const ResendVerificationPage = lazy(() => import("./pages/ResendVerificationPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FleetPage = lazy(() => import("./pages/FleetPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const SidebarLayout = lazy(() => import("./layouts/SidebarLayout"));
const AdminUserPage = lazy(() => import("./pages/admin/users/AdminUserPage"));
const AdminEmployeePage = lazy(() => import("./pages/admin/employees/AdminEmployeePage"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminQuotesPage = lazy(() => import("./pages/admin/quotes/AdminQuotesPage"));
const AdminJobsPage = lazy(() => import("./pages/admin/jobs/AdminJobsPage"));
const AdminJobEditPage = lazy(() => import("./pages/admin/jobs/AdminJobEditPage"));
const AdminCustomersPage = lazy(() => import("./pages/admin/customers/AdminCustomersPage"));
const AdminFleetRequestsPage = lazy(() => import("./pages/admin/AdminFleetRequestsPage"));
const ManualQuotePage = lazy(() => import("./pages/admin/ManualQuotePage"));
const QuotePage = lazy(() => import("./pages/QuotePage"));
const QuoteServiceStep = lazy(() => import("./pages/quote/quoteServiceStep/QuoteServiceStep"));
const QuoteVehiclePage = lazy(() => import("./pages/QuoteVehiclePage"));
const QuoteLocationStep = lazy(() => import("./pages/quote/quoteLocationStep/QuoteLocationStep"));
const QuoteContactStep = lazy(() => import("./pages/quote/quoteContactStep/QuoteContactStep"));
const QuoteScheduleStep = lazy(() => import("./pages/quote/quoteScheduleStep/QuoteScheduleStep"));
const QuoteBookStep = lazy(() => import("./pages/quote/quoteBookStep/QuoteBookStep"));
const QuoteApprovalPage = lazy(() => import("./pages/QuoteApprovalPage"));
const InvoiceTokenPage = lazy(() => import("./pages/InvoiceTokenPage"));
const EmployeeJobsPage = lazy(() => import("./pages/employee/EmployeeJobsPage"));
const EmployeeSchedulePage = lazy(() => import("./pages/employee/EmployeeSchedulePage"));
const EmployeeJobDetailsPage = lazy(() => import("./pages/employee/EmployeeJobDetailsPage"));
const EmployeeJobNotesPage = lazy(() => import("./pages/employee/EmployeeJobNotesPage"));
const EmployeeJobCompletePage = lazy(() => import("./pages/employee/EmployeeJobCompletePage"));

const PageFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <CircularProgress size={40} />
  </Box>
);


function App() {
    useViewportHeight();
    return (
        <Router>
            <PhoneSourceProvider>
            <ScrollToTop />
            <Suspense fallback={<PageFallback />}>
            <Routes>
                {/* ✅ Public Pages with AppBarLayout */}
                <Route element={<AppBarLayout />}>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/services" element={<ServicesPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/fleet" element={<FleetPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/verify" element={<EmailVerificationPage />} />
                    <Route path="/auth/change-email/confirm" element={<EmailVerificationPage />} />
                    <Route path="/resend-verification" element={<ResendVerificationPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/quote" element={<QuotePage />} />
                    <Route path="/quote/vehicle" element={<QuoteVehiclePage />} />
                    <Route path="/quote/services" element={<QuoteServiceStep />} />
                    <Route path="/quote/location" element={<QuoteLocationStep />} />
                    <Route path="/quote/schedule" element={<QuoteScheduleStep />} />
                    <Route path="/quote/contact" element={<QuoteContactStep />} />
                    <Route path="/quote/book" element={<QuoteBookStep />} />
                </Route>

                {/* ✅ Standalone Quote Approval Page (no app bar). /q/:token is the short URL for SMS. */}
                {/* More specific /estimate routes first so they don't collide with /q/:token */}
                <Route path="/q/:token/estimate" element={<InvoiceTokenPage mode="booking-estimate" />} />
                <Route path="/quote-approval/:token/estimate" element={<InvoiceTokenPage mode="booking-estimate" />} />
                <Route path="/q/:token" element={<QuoteApprovalPage />} />
                <Route path="/quote-approval/:token" element={<QuoteApprovalPage />} />

                {/* ✅ Public invoice/estimate PDF from SMS — no auth */}
                <Route path="/invoice/:token" element={<InvoiceTokenPage />} />

                {/* ✅ Protected Routes with SidebarLayout */}
                <Route
                    path="/"
                    element={
                        <PrivateRoute>
                            <SidebarLayout />
                        </PrivateRoute>
                    }
                >
                    {/* User Routes */}
                    <Route path="dashboard" element={
                        <RoleRoute allowedRoles={["USER"]}>
                            <UserDashboardPage />
                        </RoleRoute>
                    } />
                    <Route path="user/appointments" element={
                        <RoleRoute allowedRoles={["USER"]}>
                            <AppointmentsPage />
                        </RoleRoute>
                    } />
                    <Route path="user/pending-quotes" element={
                        <RoleRoute allowedRoles={["USER"]}>
                            <PendingQuotesPage />
                        </RoleRoute>
                    } />
                    <Route path="user/service-history" element={
                        <RoleRoute allowedRoles={["USER"]}>
                            <ServiceHistoryPage />
                        </RoleRoute>
                    } />
                    <Route path="user/service-history/:jobId" element={
                        <RoleRoute allowedRoles={["USER"]}>
                            <ServiceDetailsPage />
                        </RoleRoute>
                    } />
                    <Route path="user/profile" element={
                        <RoleRoute allowedRoles={["USER"]}>
                            <MyProfilePage />
                        </RoleRoute>
                    } />
                    {/* Employee Routes */}
                    <Route path="employee/schedule" element={
                        <RoleRoute allowedRoles={["EMPLOYEE"]}>
                            <EmployeeSchedulePage />
                        </RoleRoute>
                    } />
                    <Route path="employee/jobs" element={
                        <RoleRoute allowedRoles={["EMPLOYEE"]}>
                            <EmployeeJobsPage />
                        </RoleRoute>
                    } />
                    <Route path="employee/jobs/:jobId" element={
                        <RoleRoute allowedRoles={["EMPLOYEE"]}>
                            <EmployeeJobDetailsPage />
                        </RoleRoute>
                    } />
                    <Route path="employee/jobs/:jobId/notes" element={
                        <RoleRoute allowedRoles={["EMPLOYEE"]}>
                            <EmployeeJobNotesPage />
                        </RoleRoute>
                    } />
                    <Route path="employee/jobs/:jobId/complete" element={
                        <RoleRoute allowedRoles={["EMPLOYEE"]}>
                            <EmployeeJobCompletePage />
                        </RoleRoute>
                    } />

                    {/* Admin Routes */}
                    <Route path="admin/dashboard" element={
                        <RoleRoute allowedRoles={["ADMIN"]}>
                            <AdminDashboard />
                        </RoleRoute>
                    } />
                    <Route path="admin/users" element={
                        <RoleRoute allowedRoles={["ADMIN"]}>
                            <AdminUserPage />
                        </RoleRoute>
                    } />
                    <Route path="admin/employees" element={
                        <RoleRoute allowedRoles={["ADMIN"]}>
                            <AdminEmployeePage />
                        </RoleRoute>
                    } />
                    <Route path="admin/customers" element={
                        <RoleRoute allowedRoles={["ADMIN"]}>
                            <AdminCustomersPage />
                        </RoleRoute>
                    } />
                    <Route path="admin/jobs" element={
                        <RoleRoute allowedRoles={["ADMIN"]}>
                            <AdminJobsPage />
                        </RoleRoute>
                    } />
                    <Route path="admin/jobs/:jobId/edit" element={
                        <RoleRoute allowedRoles={["ADMIN"]}>
                            <AdminJobEditPage />
                        </RoleRoute>
                    } />
                    <Route path="admin/quotes" element={
                        <RoleRoute allowedRoles={["ADMIN"]}>
                            <AdminQuotesPage />
                        </RoleRoute>
                    } />
                    <Route path="admin/manual-quote" element={
                        <RoleRoute allowedRoles={["ADMIN"]}>
                            <ManualQuotePage />
                        </RoleRoute>
                    } />
                    <Route path="admin/fleet" element={
                        <RoleRoute allowedRoles={["ADMIN"]}>
                            <AdminFleetRequestsPage />
                        </RoleRoute>
                    } />
                </Route>

                {/* Fallback for unknown paths */}
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            </Suspense>
            </PhoneSourceProvider>
        </Router>
    );
}

export default App;

